<template>
    <C1FormGroup class="af-attributes-group">
        <div class="afu-flex afu-items-center afu-justify-between">
            <C1Label
                :label-for="id"
                class="af-label afu-text-black afu-block afu-mb-2 af-text--7xl afu-font-bold"
            >
                {{ label || attributeLabel }}
            </C1Label>

            <div class="afu-mb-2">
                <div
                    class="af-attributes-group-select-all afu-flex afu-items-center afu-cursor-pointer"
                    @click="!disabled && onSelectAll()"
                >
                    <FontAwesomeIcon
                        :icon="isAllSelected ? ['far', 'check-square'] : ['fas', 'square']"
                        class="af-attributes-group-select-all-icon afu-mr-2"
                        :class="{
                            'afu-text-neutral': !isAllSelected,
                            'afu-text-primary': isAllSelected,
                        }"
                    />

                    <span class="afu-text-secondary afu-text-base">{{ `${$t('label.any')} ${attributeLabel.toLocaleLowerCase()}` }}</span>
                </div>
            </div>
        </div>

        <div class="af-attributes-group-container afu-flex afu-flex-wrap afu-flex-col md:afu-flex-row md:afu-items-center afu-bg-white afu-border afu-border-solid afu-border-neutral afu-px-4 afu-pt-4 afu-pb-2 afu-rounded-card">
            <div
                v-for="option in attributeOptions"
                :key="option.value"
                class="af-attributes-group-item afu-inline-flex afu-items-center afu-rounded-btn afu-px-4 afu-py-2 sm:afu-mr-2 afu-mb-2.5 afu-cursor-pointer afu-transition afu-ease-in-out afu-duration-150 af-text--lg afu-border afu-border-solid"
                :class="{
                    'afu-text-secondary afu-bg-white afu-border-neutral': !option.is_selected,
                    'is-active afu-text-white afu-bg-primary afu-border-primary': option.is_selected,
                }"
                @click="!disabled && onSelect(option)"
            >
                <FontAwesomeIcon
                    :icon="option.is_selected ? ['far', 'check-square'] : ['fas', 'square']"
                    class="af-attributes-group-icon"
                    :class="{
                        'afu-text-neutral': !option.is_selected,
                    }"
                    style="font-size: 16px;"
                />

                <div class="af-attributes-group-label afu-ml-2 afu-text-sm">
                    {{ option.value }}
                </div>
            </div>
        </div>
    </C1FormGroup>
</template>

<script>
    import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import C1Label from '@/components/C1Components/C1Label';

    export default {
        name: 'SeekerAttributesGroup',
        components: { C1Label, C1FormGroup },

        props: {
            attributeId: {
                type: Number,
                required: true,
            },

            options: {
                type: Array,
                default: () => [],
            },

            label: {
                type: String,
                default: '',
            },

            disabled: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                id: `attribute_group_${this.attributeId}_${this._uid}`,
                attributeLabel: '',
                attributeCode: '',
                attributeOptions: [],
            };
        },

        computed: {
            ...mapState({
                $_meAttributeOptions: state => state.me.attributeOptions,
            }),

            ...mapGetters('site', {
                $_seekerFieldByCode: 'seekerFieldByCode',
            }),

            seekerAttributeOptions() {
                return this.$_meAttributeOptions.filter(
                    o => o.attribute_code === this.attributeCode,
                );
            },

            isAllSelected() {
                return (
                    this.attributeOptions.filter(o => o.is_selected).length ===
                    this.attributeOptions.length
                );
            },
        },

        watch: {
            options() {
                this.cloneAttributeOptions();
            },
        },

        created() {
            this.cloneAttributeOptions();
            this.setLabels();
        },

        methods: {
            ...mapMutations('me', {
                $_STORE_ATTRIBUTE_OPTION: 'STORE_ATTRIBUTE_OPTION',
            }),

            ...mapActions('me', {
                $_syncAttributes: 'syncAttributes',
                $_getAttributes: 'getAttributes',
            }),

            cloneAttributeOptions() {
                this.attributeOptions = this.options.map(o => ({
                    ...o,
                    is_selected:
                        this.seekerAttributeOptions.findIndex(
                            j => j.value === o.value,
                        ) >= 0,
                }));
            },

            setLabels() {
                const attributes = {
                    1: {
                        label: `${this.$t('work_preferences.job_type')}`,
                        code: 'job_type',
                    },
                    2: {
                        label: `${this.$t('work_preferences.contract_type')}`,
                        code: 'contract_type',
                    },
                    3: {
                        label: `${this.$t('label.sector')}`,
                        code: 'sector',
                    },
                    8: {
                        label: `${this.$t('label.company_size')}`,
                        code: 'company_size',
                    },
                };

                this.attributeLabel = attributes[this.attributeId].label;
                this.attributeCode = attributes[this.attributeId].code;
            },

            onSelect(attributeOption) {
                attributeOption.is_selected = !attributeOption.is_selected;

                this.syncAttributes();
            },

            onSelectAll() {
                const shouldSelect = !this.isAllSelected;
                this.attributeOptions.forEach(o => {
                    o.is_selected = shouldSelect;
                });

                this.syncAttributes();
            },

            syncAttributes() {
                const payload = {
                    attribute_option_ids: this.attributeOptions
                        .filter(o => o.is_selected)
                        .flatMap(o => o.id),
                };

                this.$_syncAttributes({ id: this.attributeId, data: payload }).then(
                    () => {
                        this.$_getAttributes().then(resp => {
                            this.$_STORE_ATTRIBUTE_OPTION(resp.data);
                        });
                    },
                );
            },
        },
    };
</script>
